<template>
  <div class="gd-section">
    <div class="gd-section-head">
      <span class="n">§4</span>
      <span class="t">{{ sectionTitle }}</span>
      <span class="d">{{ headHint }}</span>
    </div>
    <div class="gd-section-body">
      <div class="columns" v-if="readonly">
        <template v-if="type === 'sadt'">
          <div class="column col-3 col-sm-6">
            <guide-read-field label="21 · Caráter" :value="characterName"/>
          </div>
          <div class="column col-3 col-sm-6">
            <guide-read-field label="22 · Data da solicitação"
                              :value="dateText(form.request.date)"/>
          </div>
          <div class="column col-6 col-sm-12">
            <guide-read-field label="23 · Indicação clínica"
                              :value="form.service.clinicalIndication"/>
          </div>
        </template>
        <div class="column col-3 col-sm-6">
          <guide-read-field label="4 · Data da autorização"
                            :value="dateText(form.authorization.date)"/>
        </div>
        <div class="column col-4 col-sm-6">
          <guide-read-field label="5 · Senha" :value="form.authorization.password"/>
        </div>
        <div class="column col-3 col-sm-6">
          <guide-read-field label="6 · Validade da senha"
                            :value="dateText(form.authorization.validity)"/>
        </div>
      </div>
      <div class="columns" v-else>
        <template v-if="type === 'sadt'">
          <div class="column col-3 col-md-6 col-sm-12 form-group"
               :class="{'has-error': err('service.character')}">
            <label class="form-label">21 · Caráter do atendimento</label>
            <select class="form-select" v-model="form.service.character"
                    :id="fieldId('service.character')" @blur="touch('service.character')">
              <option value="">[Selecione]</option>
              <option v-for="(text, value) in characters" :value="value" :key="value">
                {{ text }}
              </option>
            </select>
            <div class="form-input-hint" v-if="err('service.character')">
              {{ hint('service.character') }}
            </div>
          </div>
          <div class="column col-3 col-md-6 col-sm-12 form-group"
               :class="{'has-error': err('request.date')}">
            <label class="form-label">
              22 · Data da solicitação
              <small v-if="!isRequired('request.date')">opcional</small>
            </label>
            <dx-input-date :id="fieldId('request.date')" class="form-input"
                           v-model="form.request.date" @blur="touch('request.date')"/>
            <div class="form-input-hint" v-if="err('request.date')">{{ hint('request.date') }}</div>
          </div>
          <div class="column col-6 col-md-12 form-group"
               :class="{'has-error': err('service.clinicalIndication')}">
            <label class="form-label">
              23 · Indicação clínica
              <small v-if="!isRequired('service.clinicalIndication')">opcional</small>
            </label>
            <input type="text" class="form-input" v-model="form.service.clinicalIndication"
                   :id="fieldId('service.clinicalIndication')"
                   @blur="touch('service.clinicalIndication')">
            <div class="form-input-hint" v-if="err('service.clinicalIndication')">
              {{ hint('service.clinicalIndication') }}
            </div>
          </div>
        </template>
        <div class="column col-3 col-md-6 col-sm-12 form-group"
             :class="{'has-error': err('authorization.date')}">
          <label class="form-label">
            4 · Data da autorização
            <small v-if="!isRequired('authorization.date')">opcional</small>
          </label>
          <dx-input-date :id="fieldId('authorization.date')" class="form-input"
                         v-model="form.authorization.date"
                         @blur="touch('authorization.date')" @input="$emit('authorization-date')"/>
          <div class="form-input-hint" v-if="err('authorization.date')">
            {{ hint('authorization.date') }}
          </div>
        </div>
        <div class="column col-4 col-md-6 col-sm-12 form-group"
             :class="{'has-error': err('authorization.password')}">
          <label class="form-label">
            5 · Senha
            <small v-if="!isRequired('authorization.password')">opcional</small>
          </label>
          <input type="text" class="form-input" v-model="form.authorization.password"
                 :id="fieldId('authorization.password')"
                 placeholder="Senha da operadora"
                 @blur="touch('authorization.password')" @input="$emit('password')">
          <div class="form-input-hint" v-if="err('authorization.password')">
            {{ hint('authorization.password') }}
          </div>
        </div>
        <div class="column col-3 col-md-6 col-sm-12 form-group"
             :class="{'has-error': err('authorization.validity')}">
          <label class="form-label">
            6 · Validade da senha
            <small v-if="!isRequired('authorization.validity')">opcional</small>
          </label>
          <dx-input-date :id="fieldId('authorization.validity')" class="form-input"
                         v-model="form.authorization.validity"
                         @blur="touch('authorization.validity')"/>
          <div class="form-input-hint" v-if="err('authorization.validity')">
            {{ hint('authorization.validity') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { characterServices } from '@/data/character-services';
import fieldMixin from '../fieldMixin';
import GuideReadField from './GuideReadField.vue';

export default {
  mixins: [fieldMixin],
  components: {
    GuideReadField,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    planSettings: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      characters: characterServices,
    };
  },
  computed: {
    authorizationSettings() {
      const sadt = this.planSettings.tiss && this.planSettings.tiss.sadt
        ? this.planSettings.tiss.sadt
        : {};
      return sadt.authorization || {};
    },
    sectionTitle() {
      return this.type === 'sadt' ? 'Solicitação e autorização' : 'Autorização';
    },
    headHint() {
      if (this.authorizationSettings.requiredPassword) return 'Senha exigida por este convênio';
      if (this.type === 'sadt') return 'Campos 21–23 e 4–6';
      return 'Campos 4–6 · opcionais na consulta';
    },
    characterName() {
      return this.form.service.character
        ? characterServices[this.form.service.character]
        : '';
    },
  },
  methods: {
    isRequired(path) {
      const node = this.field(path);
      return !!(node && node.$params && node.$params.required);
    },
    dateText(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '';
    },
  },
};
</script>
